import { log, sleep, TagId } from '@ms-ows/logging'

import {
  ActionType,
  ClickAction,
  ClickActionParams,
  SurfaceType,
} from '../../models'
import { loadScripts } from '../../utils'

type ActionName =
  | 'callback'
  | 'event'
  | 'url'
  | 'script'
  | 'unhandled'
  | 'error'
  | 'none'

const performGlobalActionCallback = (actionParams?: ClickActionParams) => {
  if (
    !actionParams ||
    (!actionParams.globalMethod && !actionParams.globalType)
  ) {
    return
  }

  // tslint:disable-next-line:no-any
  const windowObj = window as { [key: string]: any }
  type CallBackType = (params: unknown) => void

  // try executing global func - window.ObjectModel.executeAction("FreemiumUpsellDialogShowUI")
  const globalMethodPath = actionParams.globalMethod
    ? actionParams.globalMethod.trim().split('.')
    : []
  if (globalMethodPath.length > 0) {
    if (globalMethodPath[0].toLowerCase() === 'window') {
      globalMethodPath.shift()
    }

    // get function reference - eg: window['ObjectModel']['executeAction'] - returns windowObj as default
    const globalFunc = globalMethodPath.reduce(
      // tslint:disable-next-line:no-unsafe-any
      (prev, curr) => prev && prev[curr],
      windowObj
    ) as CallBackType
    if (globalFunc && globalFunc !== windowObj) {
      const delayLoad = actionParams.delayLoad || 10
      sleep(delayLoad)
        .then(() => {
          globalFunc.apply(
            null,
            actionParams.methodParams ?? (actionParams as [unknown])
          )
        })
        .catch((error: unknown) => {
          log.error(
            0x217d284a /* tag_75s7k */,
            `failed to execute global method ${globalMethodPath}`,
            error
          )
        })

      return
    }
  }

  // execute - new window.globalType(params).invocationMethod(params)
  // eg: new window.Dime(config).launch(config)
  const newableType = windowObj[actionParams.globalType || '']
  if (!newableType) {
    return
  }

  // tslint:disable-next-line: no-unsafe-any
  const newedObj = new newableType(actionParams)
  const invocableFunc: CallBackType =
    // tslint:disable-next-line: no-unsafe-any
    newedObj[actionParams.invocationMethod || '']

  if (invocableFunc) {
    const delayLoad = actionParams.delayLoad || 10
    sleep(delayLoad)
      .then(() => {
        invocableFunc.call(newedObj, actionParams.methodParams ?? actionParams)
      })
      .catch((error: unknown) => {
        log.error(
          0x2238b79e /* tag_8ol44 */,
          'failed to execute script method',
          error
        )
      })
  }
}

// tslint:disable-next-line: max-func-body-length
const performAction = (
  inputClickAction?: ClickAction,
  actionParams?: ClickActionParams
): ActionName => {
  if (!inputClickAction) {
    return 'none'
  }

  if (typeof inputClickAction === 'function') {
    inputClickAction()

    return 'callback'
  }

  let clickAction = inputClickAction.toLowerCase()
  // clickAction should in the format prefix:name. eg: event:MyEvent or script:url or http://
  // append https as default if prefix is not specified
  if (
    clickAction !== 'script' &&
    clickAction.indexOf('/') !== 0 &&
    clickAction.indexOf(':') === -1
  ) {
    clickAction = `https://${clickAction}`
  }

  if (clickAction.indexOf('http') === 0) {
    const openedWindow = window.open(clickAction)
    if (!openedWindow) {
      log.error(
        0x2238b79f /* tag_8ol45 */,
        'window.open reference is null for url button click action',
        clickAction
      )
    }

    return 'url'
  }

  if (clickAction.indexOf('event') === 0) {
    let eventName = actionParams?.eventName
    if (!eventName) {
      eventName = inputClickAction.trim().split(':')[1]
    }

    if (!eventName) {
      log.error(
        0x2238b7a0 /* tag_8ol46 */,
        'Invalid event name',
        inputClickAction
      )

      return 'error'
    }

    const eventObj = { detail: actionParams }
    document.dispatchEvent(new CustomEvent(eventName, eventObj))

    return 'event'
  }

  if (!actionParams) {
    return 'none'
  }

  if (clickAction.indexOf('script') === 0) {
    if (actionParams.script) {
      loadScripts(actionParams.script)
        .then(() => {
          performGlobalActionCallback(actionParams)
        })
        .catch(error => {
          log.error(
            0x2238b7a1 /* tag_8ol47 */,
            `Script error ${actionParams.script} ${error}`
          )
        })
    } else {
      performGlobalActionCallback(actionParams)
    }

    return 'script'
  }

  return 'unhandled'
}

export const handleClickAction = (
  tagId: TagId,
  button: ActionType,
  selector: string | undefined,
  surfaceType: keyof typeof SurfaceType,
  clickAction?: ClickAction,
  clickActionParams?: ClickActionParams,
  eventType?: string
) => {
  let action = 'error'

  try {
    action = performAction(clickAction, clickActionParams) as string
  } catch (error) {
    log.error(0x2238b7a2 /* tag_8ol48 */, 'error invoking input action')
  }

  log.userActionEvent(tagId, 'SurfaceButtonClicked', {
    customData: {
      action,
      button,
      eventType: eventType || '',
      selector,
      surfaceType,
    },
  })
}
